import { UserId, RegionId, Hash, VoxelCoord, RegionType, Receipt } from '@maco144/shared';
import {
  ClientMessage,
  ServerMessage,
  SolutionPayload,
  ConnectionState,
  NetworkEvents,
} from '@maco144/shared';
import { BrowserSparseMerkleTree } from './crypto';

type EventHandler<T extends any[] = any[]> = (...args: T) => void;

// Browser-compatible client (uses native WebSocket)
export class BrowserClient {
  private ws: WebSocket | null = null;
  private url: string;
  private state: ConnectionState = 'disconnected';
  private userId: UserId | null = null;
  private homeRegion: RegionId | null = null;
  private currentRegion: RegionId | null = null;
  private lastWorldRoot: Hash | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000;
  private listeners: Map<string, Set<EventHandler>> = new Map();
  private authToken: string | null = null; // Auth token from password verification

  constructor(url: string) {
    this.url = url;
  }

  // Event emitter methods
  on<K extends keyof NetworkEvents>(event: K, listener: NetworkEvents[K]): this {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)!.add(listener as EventHandler);
    return this;
  }

  off<K extends keyof NetworkEvents>(event: K, listener: NetworkEvents[K]): this {
    this.listeners.get(event)?.delete(listener as EventHandler);
    return this;
  }

  private emit<K extends keyof NetworkEvents>(
    event: K,
    ...args: Parameters<NetworkEvents[K]>
  ): void {
    this.listeners.get(event)?.forEach((listener) => {
      try {
        listener(...args);
      } catch (e) {
        console.error(`Error in ${event} listener:`, e);
      }
    });
  }

  once<K extends keyof NetworkEvents>(event: K, listener: NetworkEvents[K]): this {
    const wrapper = ((...args: any[]) => {
      this.off(event, wrapper as NetworkEvents[K]);
      (listener as EventHandler)(...args);
    }) as NetworkEvents[K];
    return this.on(event, wrapper);
  }

  connect(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.state !== 'disconnected') {
        reject(new Error('Already connected or connecting'));
        return;
      }

      this.state = 'connecting';
      this.ws = new WebSocket(this.url);

      this.ws.onopen = () => {
        this.state = 'connected';
        this.reconnectAttempts = 0;
        this.emit('connected');
        resolve();
      };

      this.ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data) as ServerMessage;
          this.handleMessage(message);
        } catch (e) {
          console.error('Invalid message from server:', e);
        }
      };

      this.ws.onclose = () => {
        this.handleDisconnect();
      };

      this.ws.onerror = (_err) => {
        if (this.state === 'connecting') {
          reject(new Error('Connection failed'));
        }
        this.emit('error', 'WebSocket error');
      };
    });
  }

  private handleDisconnect(): void {
    const _wasAuthenticated = this.state === 'authenticated';
    this.state = 'disconnected';
    this.ws = null;
    this.emit('disconnected');

    // Auto-reconnect
    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++;
      setTimeout(() => {
        console.log(`Reconnecting... attempt ${this.reconnectAttempts}`);
        this.connect().catch(() => {});
      }, this.reconnectDelay * this.reconnectAttempts);
    }
  }

  private send(message: ClientMessage): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message));
    }
  }

  private handleMessage(message: ServerMessage): void {
    switch (message.type) {
      case 'auth_ok':
        this.state = 'authenticated';
        this.userId = message.userId;
        this.homeRegion = message.homeRegion;
        this.emit('authenticated', message.userId, message.homeRegion);
        break;

      case 'auth_fail':
        this.emit('authFailed', message.reason);
        break;

      case 'entered':
        this.currentRegion = message.regionId;
        this.lastWorldRoot = message.worldRoot;
        this.emit('entered', message.regionId, message.worldRoot, message.present);
        break;

      case 'left':
        this.currentRegion = null;
        this.lastWorldRoot = message.worldRoot;
        this.emit('left', message.worldRoot);
        break;

      case 'denied':
        this.emit('denied', message.reason);
        break;

      case 'receipt':
        this.emit('receipt', message.receipt);
        break;

      case 'user_entered':
        this.emit('userEntered', message.regionId, message.userId, message.position);
        break;

      case 'user_left':
        this.emit('userLeft', message.regionId, message.userId);
        break;

      case 'user_moved':
        this.emit('userMoved', message.regionId, message.userId, message.position);
        break;

      case 'object_added':
        this.emit('objectAdded', message.regionId, message.object);
        break;

      case 'world_root_changed':
        this.lastWorldRoot = message.worldRoot;
        this.emit('worldRootChanged', message.worldRoot);
        break;

      case 'error':
        this.emit('error', message.message);
        break;

      // Chat events
      case 'chat_message':
        this.emit('chatMessage', message.message);
        break;

      case 'chat_history':
        this.emit('chatHistory', message.regionId, message.messages, message.hasMore);
        break;

      case 'chat_sync_offer':
        this.emit('chatSyncOffer', message.regionId, message.peerId, message.summary);
        break;

      case 'chat_summary':
        this.emit('chatSummary', message.regionId, message.summary);
        break;

      case 'anchor_ok':
        this.emit('anchorOk', message.regionId, message.anchor);
        break;

      case 'anchors':
        this.emit('anchors', message.anchors, message.activities);
        break;

      case 'channel_created':
        this.emit('channelCreated', message.channel);
        break;

      case 'server_channels':
        this.emit('serverChannels', message.serverRegionId, message.channels);
        break;

      case 'whisper_created':
        this.emit('whisperCreated', message.regionId, message.participant);
        break;

      case 'whisper_invite':
        this.emit('whisperInvite', message.regionId, message.inviter);
        break;

      case 'whispers':
        this.emit('whispers', message.whispers);
        break;

      case 'whisper_deleted':
        this.emit('whisperDeleted', message.regionId);
        break;

      case 'invite_generated':
        this.emit('inviteGenerated', message.inviteCode, message.serverId);
        break;

      case 'invite_accepted':
        this.emit('inviteAccepted', message.serverId, message.serverName);
        break;

      case 'invite_invalid':
        this.emit('inviteInvalid', message.reason);
        break;

      case 'password_verified':
        this.emit('password_verified', message.token);
        break;

      case 'password_invalid':
        this.emit('password_invalid', message.reason);
        break;

      case 'identity_authenticated':
        this.emit('identity_authenticated', message.userId, message.token);
        break;

      case 'user_communities':
        this.emit('user_communities', message.communities);
        break;

      // Group messages (identity-based communities)
      case 'user_groups':
        this.emit('user_groups', message.groups);
        break;

      case 'groups_list':
        this.emit('groups_list', message.groups);
        break;

      case 'entered_group_sub_accord':
        this.emit('entered_group_sub_accord', message.subAccordId);
        break;

      case 'my_group_role':
        this.emit('my_group_role', message.groupId, message.role);
        break;
    }
  }

  // Public API

  async authenticate(solution: SolutionPayload): Promise<{ userId: UserId; homeRegion: RegionId }> {
    return new Promise((resolve, reject) => {
      const onAuth = (userId: UserId, homeRegion: RegionId) => {
        this.off('authFailed', onFail);
        resolve({ userId, homeRegion });
      };

      const onFail = (reason: string) => {
        this.off('authenticated', onAuth);
        reject(new Error(reason));
      };

      this.once('authenticated', onAuth);
      this.once('authFailed', onFail);

      this.send({ type: 'auth', solution });
    });
  }

  async enter(regionId: RegionId, position?: VoxelCoord): Promise<Hash> {
    return new Promise((resolve, reject) => {
      const onEntered = (enteredRegion: RegionId, worldRoot: Hash) => {
        if (enteredRegion === regionId) {
          this.off('denied', onDenied);
          resolve(worldRoot);
        }
      };

      const onDenied = (reason: string) => {
        this.off('entered', onEntered);
        reject(new Error(reason));
      };

      this.once('entered', onEntered);
      this.once('denied', onDenied);

      this.send({ type: 'enter', regionId, position });
    });
  }

  leave(): void {
    this.send({ type: 'leave' });
  }

  move(position: VoxelCoord): void {
    this.send({ type: 'move', position });
  }

  place(objectType: string, position: VoxelCoord, payload: unknown): void {
    this.send({ type: 'place', objectType, position, payload });
  }

  createRegion(regionType: RegionType, isPublic: boolean, name?: string): void {
    this.send({ type: 'create_region', regionType, isPublic, name });
  }

  requestReceipt(): void {
    this.send({ type: 'request_receipt' });
  }

  listRegions(): void {
    this.send({ type: 'list_regions' });
  }

  listPresent(regionId: RegionId): void {
    this.send({ type: 'list_present', regionId });
  }

  subscribe(regionId: RegionId): void {
    this.send({ type: 'subscribe', regionId });
  }

  unsubscribe(regionId: RegionId): void {
    this.send({ type: 'unsubscribe', regionId });
  }

  // Verify a receipt locally
  verifyReceipt(receipt: Receipt): boolean {
    return BrowserSparseMerkleTree.verify(receipt.proof);
  }

  // Getters
  getState(): ConnectionState {
    return this.state;
  }

  getUserId(): UserId | null {
    return this.userId;
  }

  getHomeRegion(): RegionId | null {
    return this.homeRegion;
  }

  getCurrentRegion(): RegionId | null {
    return this.currentRegion;
  }

  getLastWorldRoot(): Hash | null {
    return this.lastWorldRoot;
  }

  // Chat-related methods
  sendChatMessage(message: any): void {
    this.send({ type: 'chat_message', message });
  }

  requestHistory(regionId: RegionId, since: number, limit: number = 100): void {
    this.send({ type: 'chat_history_request', regionId, since, limit });
  }

  requestSyncFromPeer(regionId: RegionId, peerId: string): void {
    this.send({ type: 'chat_sync_request', regionId, peerId });
  }

  requestChatSummary(regionId: RegionId): void {
    this.send({ type: 'chat_summary_request', regionId });
  }

  // Anchor-related methods
  anchorRegion(regionId: RegionId, notificationSettings?: any): void {
    this.send({ type: 'anchor_region', regionId, notificationSettings });
  }

  unanchorRegion(regionId: RegionId): void {
    this.send({ type: 'unanchor_region', regionId });
  }

  getAnchors(): void {
    this.send({ type: 'get_anchors' });
  }

  createChannel(
    parentRegionId: RegionId,
    name: string,
    channelType?: 'text' | 'voice' | 'category'
  ): void {
    this.send({ type: 'create_channel', parentRegionId, name, channelType });
  }

  getServerChannels(serverRegionId: RegionId): void {
    this.send({ type: 'get_server_channels', serverRegionId });
  }

  // Whisper/DM methods
  createWhisper(targetUser: UserId): void {
    this.send({ type: 'create_whisper', targetUser });
  }

  listWhispers(): void {
    this.send({ type: 'list_whispers' });
  }

  deleteWhisper(regionId: RegionId): void {
    this.send({ type: 'delete_whisper', regionId });
  }

  // Invite methods
  generateInvite(serverId: RegionId): void {
    this.send({ type: 'generate_invite', serverId });
  }

  joinViaInvite(inviteCode: string): void {
    this.send({ type: 'join_via_invite', inviteCode });
  }

  /**
   * Verify a PASSWORD for an existing identity
   * PASSWORD should be hashed client-side before calling this
   * Returns an auth token on success
   */
  verifyPassword(userId: UserId, passwordHash: string): Promise<string> {
    return new Promise((resolve, reject) => {
      const onVerified = (token: string) => {
        this.off('password_invalid', onInvalid);
        this.authToken = token;
        resolve(token);
      };

      const onInvalid = (reason: string) => {
        this.off('password_verified', onVerified);
        reject(new Error(reason));
      };

      // Set up single-use listeners
      this.once('password_verified', onVerified);
      this.once('password_invalid', onInvalid);

      // Send password verification request
      this.send({
        type: 'verify_password',
        userId,
        passwordHash,
      });
    });
  }

  /**
   * Authenticate an identity with NAME and PASSWORD
   * PASSWORD should be hashed client-side (PBKDF2) before calling
   * Returns auth token and sets userId if successful
   */
  authenticateIdentity(
    userId: UserId,
    name: string,
    passwordHash: string,
    adjective?: string
  ): Promise<string> {
    return new Promise((resolve, reject) => {
      const onAuthenticated = (authUserId: UserId, token: string) => {
        this.off('password_invalid', onInvalid);
        this.userId = authUserId;
        this.authToken = token;
        this.state = 'authenticated';
        this.emit('authenticated', authUserId, this.homeRegion || ('home' as any));
        resolve(token);
      };

      const onInvalid = (reason: string) => {
        this.off('identity_authenticated', onAuthenticated);
        reject(new Error(reason));
      };

      this.once('identity_authenticated', onAuthenticated);
      this.once('password_invalid', onInvalid);

      this.send({
        type: 'authenticate_identity',
        userId,
        name,
        passwordHash,
        adjective,
      });
    });
  }

  /**
   * Get the current auth token
   * Used for sending with protected operations
   */
  getAuthToken(): string | null {
    return this.authToken;
  }

  /**
   * Clear the auth token (logout)
   */
  clearAuthToken(): void {
    this.authToken = null;
  }

  // Group-related methods
  getUserGroups(adjective?: string, noun?: string): void {
    this.send({ type: 'get_user_groups', adjective, noun });
  }

  listGroups(groupType?: 'adjective' | 'noun' | 'crossover' | 'custom'): void {
    this.send({ type: 'list_groups', groupType });
  }

  enterGroupSubAccord(groupId: RegionId, subAccordType: 'chat' | 'live' | 'forums'): void {
    this.send({ type: 'enter_group_sub_accord', groupId, subAccordType });
  }

  getMyGroupRole(groupId: RegionId): void {
    this.send({ type: 'get_my_group_role', groupId });
  }

  disconnect(): void {
    this.maxReconnectAttempts = 0;
    this.ws?.close();
  }
}
